import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Spinner, Receipt, Clock, CheckCircle, Warning, CreditCard, Bank, Lightning,
  DownloadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { useTranslation } from "react-i18next";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantInvoicesPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [payDialog, setPayDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [payMethod, setPayMethod] = useState("bank");
  const [paying, setPaying] = useState(false);
  const [bankInfo, setBankInfo] = useState(null);
  const [tiPage, setTiPage] = useState(1);
  const [tiPageSize, setTiPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      const [invRes, bankRes] = await Promise.all([
        axios.get(`${API_URL}/api/fixed-expenses/invoices/tenant`),
        axios.get(`${API_URL}/api/my-customer-bank-info`).catch(() => ({ data: null })),
      ]);
      setInvoices(invRes.data);
      setBankInfo(bankRes.data);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPayDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPayMethod("bank");
    setPayDialog(true);
  };

  const downloadInvoicePdf = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/fixed-expenses/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoiceId.slice(0, 8)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF indirme basarisiz"); }
  };

  const handlePay = async () => {
    if (!selectedInvoice) return;
    setPaying(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/fixed-expenses/invoices/${selectedInvoice.id}/pay`,
        { payment_method: payMethod }
      );
      toast.success(res.data.message);
      setPayDialog(false);
      fetchData();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setPaying(false); }
  };

  const statusBadge = (status, cut) => {
    const map = {
      pending: { cls: "bg-amber-100 text-amber-700", icon: <Clock size={12} className="mr-1" />, label: t("fixed_expenses.pending") },
      overdue: { cls: "bg-red-100 text-red-700", icon: <Warning size={12} className="mr-1" />, label: t("fixed_expenses.overdue") },
      paid: { cls: "bg-emerald-100 text-emerald-700", icon: <CheckCircle size={12} className="mr-1" />, label: t("fixed_expenses.paid") },
      bypassed: { cls: "bg-slate-100 text-slate-500", icon: null, label: t("fixed_expenses.exempt") },
      pending_approval: { cls: "bg-blue-100 text-blue-700", icon: <Clock size={12} className="mr-1" />, label: t("dashboard.waiting_approval") },
    };
    const s = map[status] || { cls: "bg-slate-100", icon: null, label: status };
    return (
      <div className="flex items-center gap-1">
        <Badge className={s.cls}>{s.icon}{s.label}</Badge>
        {cut && <Badge className="bg-red-100 text-red-700 text-[10px]"><Lightning size={10} className="mr-0.5" />{t("fixed_expenses.cutoff")}</Badge>}
      </div>
    );
  };

  const unpaidCount = invoices.filter(i => ["pending", "overdue"].includes(i.status) && i.amount > 0).length;
  const totalDebt = invoices
    .filter(i => ["pending", "overdue", "pending_approval"].includes(i.status))
    .reduce((sum, i) => sum + (i.amount || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tenant-invoices-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">{t("sidebar.my_invoices")}</h1>
        <p className="text-slate-500 mt-1">Sabit gider faturalariniz ve odeme durumunuz</p>
      </div>

      {/* Summary */}
      {totalDebt > 0 && (
        <Card className="border-2 border-amber-200 bg-amber-50/50 shadow-sm">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={24} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-amber-800">
                {unpaidCount} odenmemis faturaniz var - Toplam: {totalDebt.toFixed(2)} TL
              </p>
              <p className="text-sm text-amber-600 mt-1">
                Odenmemis faturalariniz oldugu surece kWh yuklemesi yapamazsiniz. 
                Lutfen once faturalarinizi odeyin.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Receipt size={18} className="text-purple-600" /> Tum Faturalar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <Receipt size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Henuz faturaniz yok</p>
            </div>
          ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("common.period")}</TableHead>
                    <TableHead>{t("common.expense")}</TableHead>
                    <TableHead>{t("common.amount")}</TableHead>
                    <TableHead>{t("common.due_date")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.slice((tiPage - 1) * tiPageSize, tiPage * tiPageSize).map(inv => (
                    <TableRow key={inv.id} data-testid={`tinv-${inv.id}`}
                      className={inv.status === "overdue" ? "bg-red-50/30" : ""}>
                      <TableCell className="text-sm font-medium">{inv.period}</TableCell>
                      <TableCell>
                        <Badge className="bg-indigo-50 text-indigo-700 text-xs">{inv.expense_type_name}</Badge>
                      </TableCell>
                      <TableCell className="font-bold">
                        {inv.amount > 0 ? `${inv.amount.toFixed(2)} TL` : (
                          <span className="text-slate-400">0 TL (Muaf)</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {inv.due_date ? new Date(inv.due_date).toLocaleDateString("tr-TR") : "-"}
                      </TableCell>
                      <TableCell>{statusBadge(inv.status, inv.electricity_cut)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-indigo-600"
                            onClick={() => downloadInvoicePdf(inv.id)} data-testid={`pdf-${inv.id}`}
                            title={t("common.pdf")}>
                            <DownloadSimple size={14} />
                          </Button>
                        {["pending", "overdue"].includes(inv.status) && inv.amount > 0 && (
                          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => openPayDialog(inv)} data-testid={`pay-${inv.id}`}>
                            <CreditCard size={12} className="mr-1" /> Ode
                          </Button>
                        )}
                        {inv.status === "paid" && inv.paid_at && (
                          <span className="text-xs text-slate-400">
                            {new Date(inv.paid_at).toLocaleDateString("tr-TR")}
                          </span>
                        )}
                        {inv.status === "pending_approval" && (
                          <span className="text-xs text-blue-500">Musteri onayinda</span>
                        )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination total={invoices.length} page={tiPage} pageSize={tiPageSize} onPageChange={setTiPage} onPageSizeChange={setTiPageSize} />
          </>
          )}
        </CardContent>
      </Card>
      <Dialog open={payDialog} onOpenChange={setPayDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("invoices.payment_title")}</DialogTitle>
            <DialogDescription>
              {selectedInvoice?.expense_type_name} - {selectedInvoice?.period}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-4 bg-slate-50 rounded-lg text-center">
              <p className="text-xs text-slate-500">Odenecek Tutar</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{selectedInvoice?.amount?.toFixed(2)} TL</p>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Odeme Yontemi</p>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border-2 border-blue-400 bg-blue-50">
                  <Bank size={20} className="text-blue-600" />
                  <div>
                    <span className="font-medium text-sm">Havale / EFT</span>
                    <span className="text-xs text-slate-400 block">Musteri onayi gerekir</span>
                  </div>
                </label>
              </div>

              {bankInfo?.iban && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 mb-1">IBAN:</p>
                  <p className="font-mono text-sm font-bold text-blue-800">{bankInfo.iban}</p>
                  {bankInfo.bank_name && <p className="text-xs text-blue-600 mt-1">{bankInfo.bank_name}</p>}
                  {bankInfo.account_holder && <p className="text-xs text-blue-600">{bankInfo.account_holder}</p>}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDialog(false)}>{t("common.cancel")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handlePay} disabled={paying} data-testid="confirm-pay-btn">
              {paying ? t("billing.processing") : payMethod === "online" ? t("subscription.pay") : t("subscription.confirm_bank")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantInvoicesPage;
